import moment from 'moment';

const debug = false;

/**
 * A filter callback function that sorts enrichments by all, longstay, or data
 *
 * @param {*} entry
 * @param {*} filter
 */
export const behLongGenFilter = (entry, filter) => {
	if (filter === 'all') { 
		return true;
	} else if (filter === 'longstay') {
		return entry.longStayStartTracking;
	} else if (filter === 'behaviour') {
		return entry.trainingPlan.toLowerCase().trim() === 'yes';
	}
};

/**
 * Converts a Float to a string representation of a percentage
 *
 * @export
 * @param {Float} decimal
 * @param {number} [fixed=0]
 * @returns {String} percentage
 */
export function toPercent(decimal, fixed = 0) {
	return `${(decimal * 100).toFixed(fixed)}%`;
}

/**
 * a .filter callback that removes duplicates
 *
 * @param {*} item
 * @param {*} pos
 * @param {*} self
 */
export const uniqueOnlyFilter = (item, pos, self) => self.indexOf(item) === pos;

/**
 * Generates an object containing the total enrichment time for all enrichment types
 *
 * @param {Array} an array of enrichment data entries
 * @returns object containing the total enrichment time for all enrichment types
 */
export function breakdownEnrichmentTypes(data) {
	if (debug) console.time('breakdownEnrichmentTypes');

	let enrichmentTypeData = {};

	data.forEach(entry => {
		//Pull out data for the pie graph (totals by type) (ie add this dogs walk time to "walk" in the object, to eventually have a total)
		if (
			enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] === undefined
		) {
			//if there is no entry for an enrichment type, set it to zero
			enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] = 0;
		}
		enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] =
			enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] +
			entry.minutesOfEnrichment;
	});
	if (debug) console.timeEnd('breakdownEnrichmentTypes');
	return enrichmentTypeData;
}

/**
 * Returns the total enrichment time for the data provided
 *
 * @param {Array} an array of enrichment data entries
 * @returns {Number} total time
 */
export function getTotalEnrichmentTime(data) {
	if (debug) console.time('getTotalEnrichmentTime');
	const num = data.reduce(
		(accumulator, entry) => accumulator + parseFloat(entry.minutesOfEnrichment),
		0
	);
	if (debug) console.timeEnd('getTotalEnrichmentTime');
	return num;
}

/**
 * Generates an object containing the total enrichment time for each color code
 *
 * @param {Array} an array of enrichment data entries
 * @returns object containing the total enrichment time for each color code
 */
export function breakdownColorCodeData(data) {
	if (debug) console.time('breakdownColorCodeData');
	let colorCodeData = {};

	data.forEach(entry => {
		//Pull out data for the first bar-graph (total enrichment by colorCode) (IE. add this dog's enrichment time to the running tally for his color code at the time of that enrichment)
		if (colorCodeData[entry.colorCode.toLowerCase().trim()] === undefined) {
			colorCodeData[entry.colorCode.toLowerCase().trim()] = 0;
		}
		colorCodeData[entry.colorCode.toLowerCase().trim()] =
			colorCodeData[entry.colorCode.toLowerCase().trim()] +
			entry.minutesOfEnrichment;
	});
	if (debug) console.timeEnd('breakdownColorCodeData');
	return colorCodeData;
}

/**
 * Counts the unique aNumbers in the set of data provided.
 *
 * @param {Array} an array of enrichment data entries
 * @returns The number of unique anumbers in the data provided
 */
export function countUniqueAnumbers(data) {
	return data.map(entry => entry.aNumber).filter(uniqueOnlyFilter).length;
}

/**
 * Sorts the provided enrichmes by day and returns an object where
 * the keys are the date, and the value is an array of entries
 *
 * @export
 * @param {Date} Date representing the earliest enrichment
 * @param {Date} Date representing the latest enrichment
 * @param {Array} Enrichment Data
 * @returns  {Object} The enrichment data sorted by date
 */
export const animalsByDay = (function() {
	const cache = [];
	return function(
		start,
		end,
		data,
		updatePercent = function() {

		}
	) {
		if (debug) console.time('animalsByDay');
		const cached = checkCache(cache, data);
		if (cached) {
			if (debug) console.log('--cached--');
			if (debug) console.timeEnd('animalsByDay');
			return cached;
		} else {
			const startDate = moment(start);
			const endDate = moment(end);
			let diff = endDate.diff(startDate, 'days');
			if (diff <= 0) (diff = 1);
			const result = {};

			for (let i = 0; i < diff; i++) {
				const printDay = startDate.format('YYYY-MM-DD');
				const percentageToDisplay = Math.floor(i / diff * 20);
				updatePercent(percentageToDisplay);
				if (!result[printDay]) {
					result[printDay] = data.filter(dog =>
						startDate.isSame(dog.timeReturned, 'day')
					);
				}
				startDate.add(1, 'days');
			}
			cache.push({
				data: data,
				result: result
			});
			if (debug) console.timeEnd('animalsByDay');
			return result;
		}
	};
})();

/**
 * Seperates animals into arrays by date, seperated by colorcode
 *
 * @param {Array} an array of enrichment data entries
 * @returns an object containing the an object of Arrays for each color, sorted by date
 */
export function seperateAnimalsByColorAndDate(start, end, data) {
	if (debug) console.time('seperateAnimalsByColorAndDate');
	const colors = data.map(entry => entry.colorCode).filter(uniqueOnlyFilter);
	const dataByDay = animalsByDay(start, end, data);
	const result = {};
	for (let day in dataByDay) {
		const currentDay = dataByDay[day];
		colors.forEach(color => {
			if (!result[color]) {
				result[color] = {};
			}
			result[color][day] = currentDay.filter(
				entry => entry.colorCode.toLowerCase().trim() === color.toLowerCase().trim()
			);
		});
	}
	if (debug) console.timeEnd('seperateAnimalsByColorAndDate');
	return result;
}

/**
 * Returns the counts of unique aNumbers for each day, for each color
 *
 * @export
 * @param {any} dataSortedByColorThenDate
 * @returns
 */
export function countUniqueAnumbersByColor(dataSortedByColorThenDate) {
	if (debug) console.time('countUniqueAnumbersByColor');
	const colors = Object.keys(dataSortedByColorThenDate);
	const results = {};
	colors.forEach(color => {
		const dates = Object.keys(dataSortedByColorThenDate[color]);
		const dateObj = dataSortedByColorThenDate[color];
		results[color] = {};
		Object.values(dateObj).forEach((day, i) => {
			results[color][dates[i]] = countUniqueAnumbers(day);
		});
	});
	if (debug) console.timeEnd('countUniqueAnumbersByColor');
	return results;
}

/**
 * Formats our data for a multiple area-chart
 *
 * @export
 * @param {Object} an object where each value is an array
 * @returns {Array} where item is a day
 */
export function prepColorsForArea(start, end, countByColorThenDate) {
	const startDate = moment(start);
	const endDate = moment(end);
	const diff = endDate.diff(startDate, 'days');

	const result = [];

	for (let i = 0; i < diff; i++) {
		const printDay = startDate.format('YYYY-MM-DD');
		const day = {
			name: printDay
		};
		for (let color in countByColorThenDate) {
			const currentValue = countByColorThenDate[color][printDay];
			day[color] = currentValue;
		}
		result.push(day);
		startDate.add(1, 'days');
	}
	return result;
}

/**
 * Returns all colors used in the given dataset
 *
 * @export
 * @param {any} data
 * @returns array of unique color codes
 */
export function retrieveUsedColors(data) {
	return data.map(entry => entry.colorCode).filter(uniqueOnlyFilter);
}

/**
 * determines which key hold the largest value in an object
 *
 * @param {Object} and object where all keys hold a number as their value
 * @returns the key that holds the largest number
 */
export function biggestFromObjectOfNumbers(object) {
	let biggest = 0;
	let winner = false;
	for (let key in object) {
		const number = object[key];
		if (number > biggest) {
			winner = key;
		}
	}

	return winner;
}

function checkCache(cache, data) {
	let result = false;
	cache.forEach(entry => {
		if (entry.data === data) {
			result = entry.result;
		}
	});
	return result;
}

/**
 * Determines the enrichment time owed to each human type (volunteer, staff, etc)
 *
 * @param {any} data
 * @returns
 */
export function breakdownHumanContributions(data, round = false) {
	const humanTypes = data
		.map(entry => entry.walkerType)
		.filter(uniqueOnlyFilter);

	return humanTypes.map(type => {
		const val = data
			.filter(
				entry => entry.walkerType.toLowerCase().trim() === type.toLowerCase().trim()
			)
			.reduce((accumulator, entry) => accumulator + entry.minutesOfEnrichment, 0);
		return {
			label: `${type} Enrichment Contribution`,
			value: round ? Math.round(val) : val,
			color: getRandomColor()
		};
	});
}

/**
 *  returns a random color as a hex code
 *
 * @export
 * @returns {String} a random hex code
 */
export function getRandomColor() {
	var letters = '0123456789ABCDEF';
	var color = '#';
	for (var i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	return color;
}

/**
 * Calls get random color N times
 *
 * @export
 * @param {Number} n
 * @returns {Array} of color hex codes (strings)
 */
export function getNRandomColors(n) {
	const colors = [];
	for (let i = 0; i < n; i++) {
		colors.push(getRandomColor());
	}
	return colors;
}

/**
 * Transforms an object of enrichment type data to an array that
 * is friendly for rechart's pie graph
 *
 * @export
 * @param {Object} object where keys are enrichment types, values are that types's totoal enrichment contribution
 * @returns {Array} friendly array for our bar graph
 */
export function prepForPie(objOfTypes) {
	const keys = Object.keys(objOfTypes);
	const results = keys.map(type => ({
		color: getRandomColor(),
		label: type,
		value: objOfTypes[type]
	}));
	return results;
}

/**
 * Transforms an object of color data to an array that
 * is friendly for rechart's bar graph
 *
 * @export
 * @param {Object} object where keys are colors, values are that color's totoal enrichment contribution
 * @returns {Array} friendly array for our bar graph
 */
export function prepForBar(objOfColors) {
	const keys = Object.keys(objOfColors);
	const results = keys.map(color => ({
		'Minutes of Enrichment': objOfColors[color],
		'name': color
	}));

	return results;
}

/**
 * Transforms an object of dog counts into
 * an array friendly for an area graph
 *
 * @export
 * @param {Object} object where keys are dates, values are numbers
 * @returns {Array} friendly array for our area graph
 */
export function prepForArea(objOfDateCounts) {
	const keys = Object.keys(objOfDateCounts);
	const results = keys.map(date => ({
		count: objOfDateCounts[date],
		name: date,
		parse: Date.parse(date)
	}));

	return results;
}

/**
 * Returns the average enrichment time for a single enrichment
 *
 * @export
 * @param {Array} Enrichment Data
 * @returns {Number} the average of all enrichment entries
 */
export function getAverageForSingleEnrichment(data, filter = 'all') {
	const minutesOfEnrichment = data
		.filter(item => behLongGenFilter(item, filter))
		.map(entry => entry.minutesOfEnrichment);
	return (
		minutesOfEnrichment.reduce((accumulator, item) => accumulator + item, 0) /
		minutesOfEnrichment.length
	);
}

/**
 * Returns the average enrichment time for all enrichments in a day
 *
 * @export
 * @param {Array} Enrichment Data
 * @returns {Number} the average of all enrichment entries per day
 */
export function getAverageForDailyEnrichment(start, end, data, filter = 'all') {
	//breakdown data by day
	const animalsByDayResult = animalsByDay(start, end, data);
	const animalsByDayValues = Object.values(animalsByDayResult);

	//
	const averagesForAllDays = animalsByDayValues.map(dataForDay => {
		//each dataForDay is a day of enrichment objects
		// we need to determine the average for each dog, so...

		//Get only the type of dogs we care about
		const filteredArr = dataForDay.filter(item => behLongGenFilter(item, filter));

		//find all dogs aNumbers
		const aNumbers = filteredArr.map(entry => entry.aNumber).filter(uniqueOnlyFilter);

		const averagesForEachDog = aNumbers.map(aNumber => {
			const currentDataSet = filteredArr.filter(entry => entry.aNumber === parseInt(aNumber, 10));
			return (
				currentDataSet.reduce((acc, item) => acc + item.minutesOfEnrichment, 0)
			);
		});
		const averageForWholeDay = averagesForEachDog.reduce((acc, item) => acc + item, 0) /
		(averagesForEachDog.length > 0 ? averagesForEachDog.length : 1);

		return averageForWholeDay;
	});
	const dailyAverage =
		(averagesForAllDays.reduce((acc, item) => acc + item, 0)) / (averagesForAllDays.length > 0 ? averagesForAllDays.length : 1);
	return dailyAverage;
}

/**
 * Returns the average total time out of each dog in the provided data
 *
 * @export
 * @param {any} data
 * @returns {Number}
 */
export function getAverageTimeOutPerDog(data, filter = 'all') {
	if (debug) console.time('getAverageTimeOutPerDog');
	const aNumbers = data
		.filter(item => behLongGenFilter(item, filter))
		.map(animal => animal.aNumber)
		.filter(uniqueOnlyFilter);

	const result =
		aNumbers
			.map(aNumber => {
				const aNumberSpecificData = data.filter(entry => entry.aNumber === aNumber);

				return (
					aNumberSpecificData.reduce(
						(accumulator, item) => accumulator + item.minutesOfEnrichment,
						0
					) / (aNumberSpecificData.length > 0 ? aNumberSpecificData.length : 1)
				);
			})
			.reduce((accum, item) => accum + item, 0) /
			aNumbers.length >
		0 ?
			aNumbers.length :
			1;
	if (debug) console.timeEnd('getAverageTimeOutPerDog');
	return result;
}

/**
 * Returns the Total total time out of each dog in the provided data
 *
 * @export
 * @param {any} data
 * @returns {Number}
 */
export function getTotalTimeOutPerDog(data, filter = 'all') {
	if (debug) console.time('getAverageTimeOutPerDog');
	const aNumbers = data
		.filter(item => behLongGenFilter(item, filter))
		.map(animal => animal.aNumber)
		.filter(uniqueOnlyFilter);

	const result =
		((aNumbers
			.map(aNumber => {
				const aNumberSpecificData = data.filter(entry => entry.aNumber === aNumber);

				return (
					aNumberSpecificData.reduce(
						(accumulator, item) => accumulator + item.minutesOfEnrichment,
						0
					)
				);
			})
			.reduce((accum, item) => accum + item, 0)) /
			(aNumbers.length >
		0 ?
				aNumbers.length :
				1));
	if (debug) console.timeEnd('getAverageTimeOutPerDog');
	return result;
}

/**
 *	Returns a chart-ready array of averages for time out per session and per animal
 *
 * @export
 * @param {any} start
 * @param {any} end
 * @param {any} data
 */
export function getAveragesForEachDayForChart(
	start,
	end,
	data,
	filter = 'all'
) {
	if (debug) console.time('getAveragesFromEachDayForChart');
	const animalsBrokenDownByDay = animalsByDay(start, end, data);

	const dailyAverages = Object.values(animalsBrokenDownByDay).map(arr => {
		const filteredArr = arr.filter(item => behLongGenFilter(item, filter));
		return (
			filteredArr.reduce((acc, item) => acc + item.minutesOfEnrichment, 0) /
			(filteredArr.length > 0 ? filteredArr.length : 1)
		);
	});

	const averageTotalPerDog = Object.values(animalsBrokenDownByDay).map(day =>
		getTotalTimeOutPerDog(day, filter)
	);

	const startDate = moment(start);
	const endDate = moment(end);
	const diff = endDate.diff(startDate, 'days');

	const result = [];

	for (let i = 0; i < diff; i++) {
		const day = {
			'Average Length Of Each Enrichment Today': dailyAverages[i],
			'Average Total Time Out Of Cage Today': averageTotalPerDog[i],
			'date': startDate.format('YYYY-MM-DD')
		};
		result.push(day);
		startDate.add(1, 'days');
	}
	if (debug) console.timeEnd('getAveragesFromEachDayForChart');
	return result;
}

/**
 * Gets the average number of animals that participated in an activity per day over a given time span
 *
 * @export
 * @param {String} start date
 * @param {String} end date
 * @param {Array} data
 * @param {string} [type='office']
 * @returns {Number}
 */
export function getAverageAnimalsInActivityPerDay(
	start,
	end,
	data,
	type = 'office'
) {
	if (debug) console.time('getAveragesAnimalsInActivityPerDay');
	//break down the animals by day
	const animalsBrokenDownByDay = animalsByDay(start, end, data);

	const result =
		Object.values(animalsBrokenDownByDay)
			.map(day => {
				//for each day, get all the animals that appear
				const aNumbers = day.map(entry => entry.aNumber).filter(uniqueOnlyFilter);

				//Check each dog individually
				const wentToActivity = aNumbers.map(animal =>
					data.filter(entry => entry.aNumber === animal).some(
						//if they went to the activity at all, +1
						entry =>
							entry.enrichmentType.toLowerCase().trim() === type.toLowerCase().trim()
					)
				);
				return wentToActivity.filter(bool => bool).length; // reduce to only dogs that DID go at least once
			})
			.reduce((accumulator, item) => accumulator + item, 0) /
		(Object.values(animalsBrokenDownByDay).length > 0 ?
			Object.values(animalsBrokenDownByDay).length :
			1);

	if (debug) console.timeEnd('getAveragesAnimalsInActivityPerDay');
	return result;
}

export function getActivityParticipationDataForChart(
	start,
	end,
	data,
	type = 'office'
) {
	if (debug) console.time('getActivityParticipationDataForChart');
	//break down the animals by day
	const animalsBrokenDownByDay = animalsByDay(start, end, data);
	const keys = Object.keys(animalsBrokenDownByDay);
	const result = Object.values(animalsBrokenDownByDay).map((day, i) => {
		//for each day, get all the animals that appear
		const aNumbers = day.map(entry => entry.aNumber).filter(uniqueOnlyFilter);

		//Check each dog individually
		const wentToActivity = aNumbers.map(animal =>
			data.filter(entry => entry.aNumber === animal).some(
				//if they went to the activity at all, +1
				entry =>
					entry.enrichmentType.toLowerCase().trim() === type.toLowerCase().trim()
			)
		);

		const subResult = {};
		subResult[`Never went to "${type}" enrichment at all`] = wentToActivity.filter(
			bool => !bool
		).length;
		subResult[
			`Went to a "${type}" enrichment at least once`
		] = wentToActivity.filter(bool => bool).length;
		subResult['date'] = keys[i];

		return subResult; // reduce to only dogs that DID go at least once
	});
	if (debug) console.timeEnd('getActivityParticipationDataForChart');
	return result;
}

/**
 * Gets all enrichments
 *
 * @param {Array} data
 */
export function getEnrichmentTypes(data) {
	return data.map(entry => entry.enrichmentType).filter(uniqueOnlyFilter);
}

/**
 * Get the total for each category of enrichment for each dog
 *
 * @export
 * @param {any} data
 * @param {any} enrichmentTypes
 * @returns {Array} each item is an entry, containing all totals for that dog
 */
export function getTotalsForAllEnrichmentTypesForEachAnimal(
	data,
	enrichmentTypes
) {
	if (debug) console.time('getTotalsForAllEnrichmentTypesForEachAnimal');
	const aNumbers = data.map(entry => entry.aNumber).filter(uniqueOnlyFilter);
	return aNumbers.map(aNumber => {
		const result = {
			aNumber: aNumber,
			days: []
		};
		data.filter(subEntry => subEntry.aNumber === aNumber).forEach(subEntry => {
			if (!result[subEntry.enrichmentType]) {
				result[subEntry.enrichmentType] = 0;
			}
			result[subEntry.enrichmentType] =
				result[subEntry.enrichmentType] + subEntry.minutesOfEnrichment;
			result.longStayStartTracking = subEntry.longStayStartTracking;
			result.trainingPlan = subEntry.trainingPlan;
			result.days.push(moment(subEntry.timeReturned).format('YYYY-MM-DD'));
		});
		enrichmentTypes.forEach(type => {
			if (!result[type]) result[type] = 0;
		});
		result.days = result.days.filter(uniqueOnlyFilter);
		if (debug) console.timeEnd('getTotalsForAllEnrichmentTypesForEachAnimal');
		return result;
	});
}

export function getHumanTimeBreakdown(data) {
	if (debug) console.time('getHumanTimeBreakdown');
	const days = data
		.map(entry => moment(entry.timeReturned).format('YYYY-MM-DD'))
		.filter(uniqueOnlyFilter);
	const result = data
		.map(entry => entry.walkerName)
		.filter(uniqueOnlyFilter)
		.map(walker => {
			const totalTime = data
				.filter(entry => entry.walkerName === walker)
				.reduce((accumulator, item) => accumulator + item.minutesOfEnrichment, 0);

			const allTypes = data
				.filter(entry => entry.walkerName === walker)
				.map(entry => entry.walkerType);
			const uniqueTypes = allTypes.filter(uniqueOnlyFilter);
			const typeCounts = {};
			uniqueTypes.forEach(
				type =>
					(typeCounts[type] = allTypes.reduce(
						(accumulator, subType) =>
							subType === type ? accumulator + 1 : accumulator + 0,
						0
					))
			);
			const typeKeys = Object.keys(typeCounts);
			const typeValues = Object.values(typeCounts);
			const winner = Math.max(...typeValues);
			return {
				name: walker,
				totalTime,
				days,
				walkerType: typeKeys[typeValues.indexOf(winner)]
			};
		});
	if (debug) console.timeEnd('getHumanTimeBreakdown');
	return result;
}
